/**
 * Category API Service
 * Service layer for category-related API calls (FLAT party categories —
 * the two-domain rule: NOT the 5-level product taxonomy).
 *
 * B2.6'-fe-seam: `list`/`getById` gain a live-module leg (the `masters`
 * key, client.ts) executing the B0.7' contract operations against
 * `/graphql` (masters.operations.ts → masters.gql-map.ts). Signatures
 * unchanged.
 *
 * CU-1a masters flip: `create` + `toggleStatus` join the live leg
 * (partyCategoryCreate / partyCategorySetActive) and the list leg plumbs
 * search/isActive/typeId filter variables. The F2.4 dual-dialect param
 * interim is REMOVED in this slice: gold REST loses masters duty at CU-1,
 * so the mock/live contract's ONE camelCase dialect is all the REST leg
 * speaks (categorySingleDialect pin test guards the regression). update /
 * getCompanies / change-category remain mock-seam known-holes
 * (scripts/parity/known-holes.md) pending their spec-by-mock authoring.
 */

import { isAxiosError } from 'axios';
import apiClient from './axios.config';
import { isLiveModule } from './client';
import { refuseUnobservableMastersWrite } from './mastersWriteReach';
import {
  GraphQLTransportError,
  gqlMutate,
  gqlRequest,
  throwOnUserErrors,
} from './graphql.client';
import {
  mastersOperations,
  type CategoryDetailPageResult,
  type CategoryDetailVariables,
  type CategoryListPageResult,
  type MastersListVariables,
  type PartyCategoryCreateResult,
  type PartyCategorySetActiveResult,
} from './masters.operations';
import {
  CATEGORY_LIST_PLUMBED_KEYS,
  listConstraintsDroppedOnLive,
  mapPartyCategoryDetailNode,
  mapPartyCategoryListNode,
  mapPartyCategoryWriteEcho,
  toFirstVariable,
  toLegacyPagination,
  toPartyCategoryFilterInput,
} from './masters.gql-map';
import { API_ROUTES } from '@/constants/api.constants';
import type {
  CategoryListResponse,
  CategoryDetailResponse,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CategoryListParams,
  CategoryCompaniesResponse,
  AffectedVendor,
  ChangeCategoryResponse,
} from '@/types/api/category';

// The customers domain has no backend surface yet (drift D-XC-7).
// Only "route does not exist" responses count as domain-unavailable;
// transient failures (5xx, timeouts) must keep propagating.
const isEndpointMissingError = (error: unknown): boolean =>
  isAxiosError(error) &&
  (error.response?.status === 404 || error.response?.status === 501);

/**
 * Transform API response to normalize 'types' -> 'typeInfo'
 * API returns 'types' but frontend expects 'typeInfo'
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformCategory = (raw: any) => ({
  categoryId: raw.categoryId ?? 0,
  category: raw.category ?? '',
  typeInfo: raw.types || raw.typeInfo || [],
  description: raw.description ?? '',
  color: raw.color ?? '',
  isActive: raw.isActive ?? true,
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
});

// F2.4 REMOVED at CU-1a (D-XC-2/D-CAT-3): the dual-dialect param interim
// (snake_case `page_size`/`sort_by`/`sort_order` duplicated beside the
// camelCase contract params for the gold REST handler) is retired — gold
// loses masters duty at the CU-1 flip, and the mock/live contract speaks
// ONE camelCase dialect. `categorySingleDialect` (categoryApi vitest) is
// the no-fallback pin: a reintroduced snake_case duplication fails loudly.

/**
 * Category API service object
 */
export const categoryApi = {
  /**
   * GET /api/categories
   * Fetch paginated categories with optional filters
   */
  list: async (params?: CategoryListParams): Promise<CategoryListResponse> => {
    if (isLiveModule('masters')) {
      // CU-1a: search/isActive/typeId plumb into the SDL filter; sort +
      // cursor plumbing remains for the B2.7' completion pass (the SDL
      // models no party-category sort facet).
      const dropped = listConstraintsDroppedOnLive(params, CATEGORY_LIST_PLUMBED_KEYS);
      if (dropped.length > 0) {
        console.warn(
          `[categoryApi.list] live leg drops [${dropped.join(', ')}] until the B2.7' completion pass plumbs sort/cursor variables`
        );
      }
      const result = await gqlRequest<CategoryListPageResult, MastersListVariables>(
        mastersOperations.categoryList,
        {
          first: toFirstVariable(params?.pageSize),
          after: null,
          filter: toPartyCategoryFilterInput(params) ?? null,
        }
      );
      return {
        data: result.partyCategories.edges.map((edge) => mapPartyCategoryListNode(edge.node)),
        pagination: toLegacyPagination(result.partyCategories.totalCount, params?.pageSize),
      };
    }
    const response = await apiClient.get<CategoryListResponse>(API_ROUTES.CATEGORIES, {
      // F2.4 removed at CU-1a: ONE camelCase dialect (the typed contract).
      params,
    });
    // Transform 'types' -> 'typeInfo' for each category item
    const transformedData = {
      data: (response.data.data || []).map(transformCategory),
      pagination: {
        page: response.data.pagination.page ?? 1,
        pageSize: response.data.pagination.pageSize ?? 10,
        total: response.data.pagination.total ?? 0,
        totalPages: response.data.pagination.totalPages ?? 0,
      },
    };
    return transformedData;
  },

  /**
   * GET /api/categories/:categoryId
   * Fetch single category details by ID
   */
  getById: async (categoryId: number): Promise<CategoryDetailResponse> => {
    if (isLiveModule('masters')) {
      // membersFirst pins the operation's members window; the legacy detail
      // shape carries no member rows, so the smallest contract-valid window
      // rides here (the members panel wires up at the CU-1 flip).
      const result = await gqlRequest<CategoryDetailPageResult, CategoryDetailVariables>(
        mastersOperations.categoryDetail,
        { id: String(categoryId), membersFirst: 1 }
      );
      if (!result.partyCategory) {
        throw new GraphQLTransportError({
          message: `Category ${categoryId} not found`,
          graphQLErrors: ['NOT_FOUND'],
          status: 404,
        });
      }
      return mapPartyCategoryDetailNode(result.partyCategory);
    }
    const response = await apiClient.get<CategoryDetailResponse>(
      `${API_ROUTES.CATEGORIES}/${categoryId}`
    );
    // Transform 'types' -> 'typeInfo'
    return transformCategory(response.data) as CategoryDetailResponse;
  },

  /**
   * POST /api/categories
   * Create a new category
   */
  create: async (data: CreateCategoryRequest): Promise<CategoryDetailResponse> => {
    if (isLiveModule('masters')) {
      // Typed-contract field map (CreateCategoryRequest.category → SDL name;
      // the mock handler's legacy {name, notes} read is the D-CAT-6 mock-spec
      // defect, register-listed — the live leg follows the TYPES).
      const result = await gqlMutate<PartyCategoryCreateResult>(
        mastersOperations.partyCategoryCreate,
        {
          input: {
            name: data.category,
            color: data.color,
            description: data.description,
            typeIds: data.typeIds.length > 0 ? data.typeIds : undefined,
          },
        }
      );
      throwOnUserErrors(result.partyCategoryCreate.userErrors, 'partyCategoryCreate');
      if (!result.partyCategoryCreate.partyCategory) {
        throw new GraphQLTransportError({
          message: 'partyCategoryCreate returned neither a category nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      return mapPartyCategoryWriteEcho(
        result.partyCategoryCreate.partyCategory
      ) as CategoryDetailResponse;
    }
    const response = await apiClient.post<CategoryDetailResponse>(
      API_ROUTES.CATEGORIES,
      data
    );
    // Transform 'types' -> 'typeInfo'
    return transformCategory(response.data) as CategoryDetailResponse;
  },

  /**
   * PUT /api/categories/:categoryId
   * Update existing category
   */
  update: async (
    categoryId: number,
    data: UpdateCategoryRequest
  ): Promise<CategoryDetailResponse> => {
    refuseUnobservableMastersWrite('categoryApi.update');
    const response = await apiClient.put<CategoryDetailResponse>(
      `${API_ROUTES.CATEGORIES}/${categoryId}`,
      data
    );
    // Transform 'types' -> 'typeInfo'
    return transformCategory(response.data) as CategoryDetailResponse;
  },

  /**
   * DELETE /api/categories/:categoryId
   * Delete a category
   */
  delete: async (categoryId: number): Promise<void> => {
    refuseUnobservableMastersWrite('categoryApi.delete');
    await apiClient.delete(`${API_ROUTES.CATEGORIES}/${categoryId}`);
  },

  /**
   * PATCH /api/categories/:categoryId/toggle
   * Toggle category active status
   */
  toggleStatus: async (categoryId: number, isActive: boolean): Promise<void> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<PartyCategorySetActiveResult>(
        mastersOperations.partyCategorySetActive,
        { input: { categoryId: String(categoryId), isActive } }
      );
      throwOnUserErrors(result.partyCategorySetActive.userErrors, 'partyCategorySetActive');
      if (!result.partyCategorySetActive.partyCategory) {
        throw new GraphQLTransportError({
          message: 'partyCategorySetActive returned neither a category nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      // Legacy signature is void; the mock wire echoes the toggled row —
      // returning it keeps the wire twin for the L2 gateway leg.
      return mapPartyCategoryWriteEcho(
        result.partyCategorySetActive.partyCategory
      ) as unknown as void;
    }
    const response = await apiClient.patch(`${API_ROUTES.CATEGORIES}/${categoryId}/toggle`, {
      isActive,
    });
    return response.data;
  },

  /**
   * GET /api/categories/:categoryId/companies
   * Get companies (vendors) in a category
   * Used to show affected vendors when deactivating a category
   */
  getCompanies: async (
    categoryId: number,
    params?: { typeId?: number; pageSize?: number | string }
  ): Promise<CategoryCompaniesResponse> => {
    const response = await apiClient.get<CategoryCompaniesResponse>(
      `${API_ROUTES.CATEGORIES}/${categoryId}/companies`,
      {
        params: {
          typeId: params?.typeId ?? 1, // Default to vendors (typeId=1)
          pageSize: params?.pageSize ?? 'all', // Get all by default
        },
      }
    );
    return response.data;
  },

  /**
   * Get companies (vendors and customers) affected by deactivating a category
   * Maps the response to AffectedVendor format
   *
   * The customers domain has no backend surface yet (drift D-XC-7); its
   * legs degrade to vendor-only scope when the endpoint is ABSENT
   * (drift D-CAT-17) instead of failing the whole dialog. Transient
   * failures (5xx, network) still throw — only a missing route is
   * treated as "domain unavailable". Callers surface the degradation
   * via the returned `customersUnavailable` flag.
   */
  getAffectedVendors: async (
    categoryId: number
  ): Promise<{ vendors: AffectedVendor[]; count: number; customersUnavailable: boolean }> => {
    // Fetch vendors from categories endpoint (typeId=1)
    // Fetch customers from customers endpoint (categories endpoint doesn't support typeId=2)
    const [vendorsResult, customersResult] = await Promise.allSettled([
      categoryApi.getCompanies(categoryId, { typeId: 1, pageSize: 'all' }),
      apiClient.get<{ data: Array<{ customerId: number; customerName: string }>; pagination: { total: number } }>(
        API_ROUTES.CUSTOMERS,
        { params: { categoryIds: String(categoryId), isActive: 3, pageSize: 'all' } }
      ),
    ]);

    // Vendors are the core of the dialog — a failure there is a real error
    if (vendorsResult.status === 'rejected') {
      throw vendorsResult.reason;
    }
    const vendorsResponse = vendorsResult.value;

    // Map vendors from categories endpoint
    const vendors: AffectedVendor[] = vendorsResponse.data.map((company) => ({
      id: company.vendorId || company.id,
      companyId: `VEN-${String(company.vendorId || company.id).padStart(4, '0')}`,
      name: company.companyName,
      type: 'vendor' as const,
    }));

    // Map customers from customers endpoint; only a missing route degrades
    if (customersResult.status === 'rejected') {
      if (!isEndpointMissingError(customersResult.reason)) {
        throw customersResult.reason;
      }
      console.warn(
        '[categoryApi.getAffectedVendors] customers endpoint unavailable — returning vendors only:',
        customersResult.reason
      );
    }
    const customersData =
      customersResult.status === 'fulfilled' ? (customersResult.value.data?.data ?? []) : [];
    const customers: AffectedVendor[] = customersData.map((customer) => ({
      id: customer.customerId,
      companyId: `CUS-${String(customer.customerId).padStart(4, '0')}`,
      name: customer.customerName,
      type: 'customer' as const,
    }));

    // Combine and return
    const allCompanies = [...vendors, ...customers];
    const customersTotal =
      customersResult.status === 'fulfilled'
        ? (customersResult.value.data?.pagination?.total || customersData.length)
        : 0;
    return {
      vendors: allCompanies,
      // Real BE emits a zero-value pagination object on pageSize:'all'
      // (drift D-CAT-13) — fall back to the listed rows
      count: (vendorsResponse.pagination?.total || vendors.length) + customersTotal,
      customersUnavailable: customersResult.status === 'rejected',
    };
  },

  /**
   * POST /api/categories/change-category
   * Batch replace category for all vendors and customers in a category
   */
  batchReplace: async (
    fromCategoryId: number,
    toCategoryId: number
  ): Promise<{ updatedCount: number; customersSkipped: boolean }> => {
    refuseUnobservableMastersWrite('categoryApi.batchReplace');
    // 1. Read the customers leg FIRST (no backend endpoint for batch —
    // it's done per-customer below). Reading before the vendor mutation
    // means an unavailable customers domain (drift D-XC-7/D-CAT-17)
    // degrades to vendor-only scope instead of dying AFTER the vendor
    // mutation was already sent with no rollback. Only a missing route
    // degrades; transient failures throw here, before anything mutated.
    let customersToUpdate: Array<{ customerId: number; categories: Array<{ categoryId: number }> }> = [];
    let customersSkipped = false;
    try {
      const customersResponse = await apiClient.get<{
        data: Array<{ customerId: number; categories: Array<{ categoryId: number }> }>;
      }>(API_ROUTES.CUSTOMERS, {
        params: { categoryIds: String(fromCategoryId), isActive: 3, pageSize: 'all' },
      });
      customersToUpdate = customersResponse.data?.data || [];
    } catch (error) {
      if (!isEndpointMissingError(error)) {
        throw error;
      }
      customersSkipped = true;
      console.warn(
        '[categoryApi.batchReplace] customers endpoint unavailable — replacing for vendors only:',
        error
      );
    }

    // 2. Replace category for vendors (backend endpoint)
    const vendorResponse = await apiClient.post<ChangeCategoryResponse>(
      `${API_ROUTES.CATEGORIES}/change-category`,
      {
        fromId: fromCategoryId,
        toId: toCategoryId,
      }
    );
    const vendorCount = vendorResponse.data.affectedCount;

    // 3. Replace category for each customer found in step 1
    let customerCount = 0;

    // Update each customer's categories
    for (const customer of customersToUpdate) {
      try {
        // Get full customer details first
        const customerDetail = await apiClient.get<{
          customerId: number;
          customerName: string;
          individual: boolean;
          categories: Array<{ categoryId: number }>;
          localTaxId: string;
          country: string;
          stateId: number;
          cityId: number;
          address: string;
          postcode: string;
        }>(`${API_ROUTES.CUSTOMERS}/${customer.customerId}`);

        const detail = customerDetail.data;

        // Replace old categoryId with new categoryId
        const newCategoryIds = detail.categories
          .map((cat) => (cat.categoryId === fromCategoryId ? toCategoryId : cat.categoryId))
          .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

        // Update customer with new categories (PATCH supports partial update)
        await apiClient.patch(`${API_ROUTES.CUSTOMERS}/${customer.customerId}`, {
          categoryIds: newCategoryIds,
        });

        customerCount++;
      } catch (error) {
        console.error(`Failed to update customer ${customer.customerId}:`, error);
      }
    }

    return { updatedCount: vendorCount + customerCount, customersSkipped };
  },
};

export default categoryApi;
